// Helper functions for dynamic PDF table generation
import { formatCurrency } from "../../utils/Utility";
import jsPDF from "jspdf";
import "jspdf-autotable";
/**
 * Calculate dynamic column widths based on content type and available space
 * @param {Array} columns - Array of column configurations
 * @param {number} availableWidth - Total available width for the table
 * @param {Array} tableData - Table data to analyze for content length
 * @returns {Object} Column styles object for autoTable
 */
function calculateDynamicColumnWidths(columns, availableWidth, tableData = []) {
  const columnStyles = {};
  let totalFixedWidth = 0;
  const flexibleColumns = [];

  // First pass: assign fixed widths for specific column types
  columns.forEach((col, index) => {
    let width = null;

    switch (col.type) {
      case "currency":
        width = 100; // Fixed width for currency
        break;
      case "number":
        width = 80; // Fixed width for numbers
        break;
      case "date":
        width = 100; // Fixed width for dates
        break;
      default:
        // Text and textarea columns are flexible
        flexibleColumns.push({ index, column: col });
        break;
    }

    if (width) {
      columnStyles[index] = {
        cellWidth: width,
        halign: "center",
      };
      totalFixedWidth += width;
    }
  });

  // Second pass: distribute remaining width among flexible columns
  const remainingWidth = availableWidth - totalFixedWidth;
  const flexWidth =
    flexibleColumns.length > 0 ? remainingWidth / flexibleColumns.length : 0;

  flexibleColumns.forEach(({ index, column }) => {
    let width = flexWidth;

    // Adjust width based on column type
    if (column.type === "textarea") {
      width = Math.max(150, width); // Minimum width for textarea
    } else {
      width = Math.max(80, width); // Minimum width for text
    }

    columnStyles[index] = {
      cellWidth: width,
      halign: "center",
    };
  });

  return columnStyles;
}

/**
 * Process table data based on column types
 * @param {Array} items - Raw table items
 * @param {Array} columns - Column configurations
 * @returns {Object} Processed table data and metadata
 */
function processDynamicTableData(items, columns) {
  let total = 0;
  let hasTotal = false;
  let hasCurrencyColumn = columns.some((col) => col.type === "currency");

  const tableData = items.map((item) => {
    return columns.map((col) => {
      const value = item[col.key];

      switch (col.type) {
        case "currency":
          const price = Number(value) || 0;
          total += price;
          hasTotal = true;
          return formatCurrency(price.toFixed(2));

        case "number":
          return Number(value) || 0;

        case "date":
          if (value) {
            try {
              return new Date(value).toLocaleDateString();
            } catch (e) {
              return value || "";
            }
          }
          return "";

        case "textarea":
        case "text":
        default:
          return value || "";
      }
    });
  });

  // Add total row if there are currency columns and we have a total
  if (hasTotal && hasCurrencyColumn) {
    const totalRow = columns.map((col, index) => {
      if (col.type === "currency") {
        return formatCurrency(total.toFixed(2));
      } else if (index === 0) {
        return "Total Amount";
      } else {
        return "";
      }
    });
    tableData.push(totalRow);
  }

  return {
    tableData,
    hasTotal: hasTotal && hasCurrencyColumn,
    total,
  };
}

/**
 * Enhanced table cell styling function
 * @param {Object} data - autoTable cell data
 * @param {Array} columns - Column configurations
 * @param {boolean} hasTotal - Whether table has a total row
 * @param {number} totalRowIndex - Index of the total row
 */
function styleDynamicTableCell(data, columns, hasTotal, totalRowIndex) {
  // Style total row if it exists
  if (hasTotal && data.section === "body" && data.row.index === totalRowIndex) {
    if (data.column.index === 0) {
      // First column of total row - make bold and right-align
      data.cell.styles.font = "times";
      data.cell.styles.fontStyle = "bold";
      data.cell.styles.halign = "right";
    }
  }

  // Style currency cells
  if (typeof data.cell.raw === "string" && data.cell.raw.startsWith("₱")) {
    data.cell.styles.font = "Inter-Regular";
    data.cell.styles.fontStyle = "normal";
  }

  // Style based on column type
  const column = columns[data.column.index];
  if (column) {
    switch (column.type) {
      case "number":
      case "currency":
        data.cell.styles.halign = "center";
        break;
      case "textarea":
        // Allow text wrapping for textarea columns
        data.cell.styles.cellPadding = { top: 4, right: 4, bottom: 4, left: 4 };
        data.cell.styles.valign = "top";
        break;
      case "date":
        data.cell.styles.halign = "center";
        break;
      default:
        data.cell.styles.halign = "center";
        break;
    }
  }
}

/**
 * Main function to generate dynamic table in PDF
 * @param {Object} doc - jsPDF document instance
 * @param {Object} formData - Form data containing table information
 * @param {number} currentY - Current Y position in PDF
 * @param {number} contentMargin - PDF content margin
 * @param {number} pageWidth - PDF page width
 * @returns {number} Updated Y position after table
 */
function generateDynamicTable(
  doc,
  formData,
  currentY,
  contentMargin,
  pageWidth
) {
  const footerMargin = 80;
  // Check if table should be included
  const shouldIncludeTable =
    formData.tableIncluded === true &&
    formData.items &&
    formData.items.length > 0 &&
    formData.tableConfig &&
    formData.tableConfig.customColumns;

  if (!shouldIncludeTable) {
    return currentY;
  }

  const tableConfig = formData.tableConfig;
  const columns = tableConfig.customColumns;

  // Generate table headers from dynamic columns
  const tableColumnHeaders = columns.map((col) => col.header);

  // Calculate available width and column styles
  const availableWidth = pageWidth - 2 * contentMargin;
  const columnStyles = calculateDynamicColumnWidths(
    columns,
    availableWidth,
    formData.items
  );

  // Process table data
  const { tableData, hasTotal, total } = processDynamicTableData(
    formData.items,
    columns
  );

  // Generate the table
  doc.autoTable({
    startY: currentY,
    head: [tableColumnHeaders],
    body: tableData,
    styles: {
      fontSize: 10,
      halign: "center",
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineWidth: 0.1,
      fontStyle: "bold",
    },
    theme: "grid",
    margin: { left: contentMargin, right: contentMargin, bottom: footerMargin },
    columnStyles: columnStyles,
    tableLineWidth: 0.1,
    tableLineColor: [0, 0, 0],
    didParseCell: function (data) {
      styleDynamicTableCell(data, columns, hasTotal, tableData.length - 1);
    },
  });

  return doc.lastAutoTable.finalY + 30;
}

// Enhanced function to parse HTML including tables
function parseHTMLToParagraphs(htmlString) {
  const container = document.createElement("div");
  container.innerHTML = htmlString;

  // Temporarily attach to DOM to get computed styles
  document.body.appendChild(container);

  const paragraphs = [];

  const countLeadingWhitespace = (element) => {
    const firstTextNode = getFirstTextNode(element);
    if (!firstTextNode) {
      return { spaces: 0, tabs: 0, nbsp: 0, totalIndent: 0 };
    }

    const text = firstTextNode.textContent;
    let spaces = 0;
    let tabs = 0;
    let nbsp = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const charCode = text.charCodeAt(i);

      if (char === "\t") {
        tabs++;
      } else if (char === " " && charCode === 32) {
        spaces++;
      } else if (charCode === 160) {
        nbsp++;
      } else if (char !== " " && charCode !== 160) {
        break;
      }
    }

    const totalSpaces = spaces + nbsp + tabs * 4;
    const totalIndent = Math.floor(totalSpaces / 4);

    return { spaces, tabs, nbsp, totalIndent, totalSpaces };
  };

  const getFirstTextNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node;
    }
    for (let child of node.childNodes) {
      const textNode = getFirstTextNode(child);
      if (textNode) return textNode;
    }
    return null;
  };

  const getAlignment = (element) => {
    const classList = element.className || "";
    if (classList.includes("ql-align-center")) return "center";
    if (classList.includes("ql-align-right")) return "right";
    if (classList.includes("ql-align-justify")) return "justify";

    const computedStyle = window.getComputedStyle(element);
    if (computedStyle.textAlign) {
      return computedStyle.textAlign;
    }

    return "left";
  };

  // Enhanced function to parse tables with full style preservation
  const parseHTMLTableToData = (tableElement) => {
    const rows = tableElement.querySelectorAll("tr");
    const tableData = {
      type: "table",
      headers: [],
      body: [],
      hasHeader: false,
    };

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll("th, td");
      const rowData = [];

      cells.forEach((cell) => {
        const cellText = cell.textContent.trim();
        const computedStyle = window.getComputedStyle(cell);

        // Check for bold styling
        const isBold =
          cell.querySelector("strong, b") !== null ||
          computedStyle.fontWeight === "bold" ||
          parseInt(computedStyle.fontWeight) >= 700;

        // Get background color
        const bgColor = computedStyle.backgroundColor;
        const hasBgColor =
          bgColor &&
          bgColor !== "rgba(0, 0, 0, 0)" &&
          bgColor !== "transparent";

        // Get text color
        const textColor = computedStyle.color;

        // Get alignment
        const align = computedStyle.textAlign || "center";

        // Get border info
        const borderWidth = parseFloat(computedStyle.borderWidth) || 1;
        const borderColor = computedStyle.borderColor || "rgb(0, 0, 0)";

        rowData.push({
          text: cellText,
          isBold: isBold,
          colSpan: parseInt(cell.getAttribute("colspan") || "1"),
          rowSpan: parseInt(cell.getAttribute("rowspan") || "1"),
          align: align,
          backgroundColor: hasBgColor ? bgColor : null,
          textColor: textColor,
          borderWidth: borderWidth,
          borderColor: borderColor,
        });
      });

      // First row with th elements is header
      if (rowIndex === 0 && row.querySelector("th")) {
        tableData.headers = rowData;
        tableData.hasHeader = true;
      } else {
        tableData.body.push(rowData);
      }
    });

    return tableData;
  };

  container.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();

      // ---- HANDLE SIGNATURE BLOCK START MARKER ----
      if (
        node.tagName === "DIV" &&
        (node.getAttribute("data-marker") === "signature-block-start" ||
          node.getAttribute("data-marker") === "centered-block")
      ) {
        const signatureContainer = node.nextElementSibling;

        if (signatureContainer) {
          // find rows (children with display:flex)
          const rows = [...signatureContainer.children].filter((child) => {
            const styles = child.getAttribute("style") || "";
            return styles.includes("display:flex");
          });

          rows.forEach((row) => {
            const items = [...row.children].map((div) => {
              // First <p> contains the name (may have <strong><u> or just <u>)
              const nameElement = div.querySelector("p:first-of-type");
              // Second <p> contains the label
              const labelElement = div.querySelector("p:nth-of-type(2)");

              // Get name with styling - parse the entire first paragraph
              let nameWords = [];
              if (nameElement) {
                nameWords = parseNodeToSegments(nameElement, {}, true);
              }

              // Get label with styling - parse the second paragraph
              let labelWords = [];
              if (labelElement) {
                labelWords = parseNodeToSegments(labelElement, {}, true);
              }

              return {
                nameWords,
                labelWords,
              };
            });

            paragraphs.push({
              type: "sig-row",
              items,
            });
          });
        }

        return;
      }
      // ----------------------------------------------------

      if (tagName === "p") {
        const whitespace = countLeadingWhitespace(node);
        const paragraphWords = parseNodeToSegments(node, {}, true);
        if (paragraphWords.length > 0) {
          paragraphs.push({
            type: "paragraph",
            words: paragraphWords,
            indentLevel: whitespace.totalIndent,
            totalSpaces: whitespace.totalSpaces,
            leadingSpaces: whitespace.spaces,
            leadingTabs: whitespace.tabs,
            leadingNbsp: whitespace.nbsp,
            alignment: getAlignment(node),
          });
        }
      } else if (
        tagName === "h1" ||
        tagName === "h2" ||
        tagName === "h3" ||
        tagName === "h4" ||
        tagName === "h5" ||
        tagName === "h6"
      ) {
        const whitespace = countLeadingWhitespace(node);
        const headerWords = parseNodeToSegments(node, {}, true);
        if (headerWords.length > 0) {
          paragraphs.push({
            type: "header",
            level: parseInt(tagName.substring(1)),
            words: headerWords,
            indentLevel: whitespace.totalIndent,
            totalSpaces: whitespace.totalSpaces,
            leadingSpaces: whitespace.spaces,
            leadingTabs: whitespace.tabs,
            leadingNbsp: whitespace.nbsp,
            alignment: getAlignment(node),
          });
        }
      } else if (tagName === "blockquote") {
        // Handle blockquote elements
        node.childNodes.forEach((childNode) => {
          if (childNode.nodeType === Node.ELEMENT_NODE) {
            const childTag = childNode.tagName.toLowerCase();

            if (childTag === "p") {
              const whitespace = countLeadingWhitespace(childNode);
              const paragraphWords = parseNodeToSegments(childNode, {}, true);
              if (paragraphWords.length > 0) {
                paragraphs.push({
                  type: "blockquote",
                  words: paragraphWords,
                  indentLevel: whitespace.totalIndent,
                  totalSpaces: whitespace.totalSpaces,
                  leadingSpaces: whitespace.spaces,
                  leadingTabs: whitespace.tabs,
                  leadingNbsp: whitespace.nbsp,
                  alignment: getAlignment(childNode),
                });
              }
            }
          }
        });
      } else if (tagName === "ol" || tagName === "ul") {
        const listItems = node.querySelectorAll("li");
        listItems.forEach((li, index) => {
          const whitespace = countLeadingWhitespace(li);
          const itemWords = parseNodeToSegments(li, {}, true);
          if (itemWords.length > 0) {
            const dataList = li.getAttribute("data-list");

            if (dataList === "bullet") {
              paragraphs.push({
                type: "unordered-list",
                words: itemWords,
                indentLevel: whitespace.totalIndent,
                totalSpaces: whitespace.totalSpaces,
                leadingSpaces: whitespace.spaces,
                leadingTabs: whitespace.tabs,
                leadingNbsp: whitespace.nbsp,
                alignment: getAlignment(li),
              });
            } else if (dataList === "ordered") {
              paragraphs.push({
                type: "ordered-list",
                words: itemWords,
                number: index + 1,
                indentLevel: whitespace.totalIndent,
                totalSpaces: whitespace.totalSpaces,
                leadingSpaces: whitespace.spaces,
                leadingTabs: whitespace.tabs,
                leadingNbsp: whitespace.nbsp,
                alignment: getAlignment(li),
              });
            } else {
              if (tagName === "ol") {
                paragraphs.push({
                  type: "ordered-list",
                  words: itemWords,
                  number: index + 1,
                  indentLevel: whitespace.totalIndent,
                  totalSpaces: whitespace.totalSpaces,
                  leadingSpaces: whitespace.spaces,
                  leadingTabs: whitespace.tabs,
                  leadingNbsp: whitespace.nbsp,
                  alignment: getAlignment(li),
                });
              } else {
                paragraphs.push({
                  type: "unordered-list",
                  words: itemWords,
                  indentLevel: whitespace.totalIndent,
                  totalSpaces: whitespace.totalSpaces,
                  leadingSpaces: whitespace.spaces,
                  leadingTabs: whitespace.tabs,
                  leadingNbsp: whitespace.nbsp,
                  alignment: getAlignment(li),
                });
              }
            }
          }
        });
      } else if (tagName === "table") {
        // Parse and add table with full styling
        const tableData = parseHTMLTableToData(node);
        paragraphs.push(tableData);
      } else if (tagName === "hr") {
        paragraphs.push({
          type: "page-break",
        });
      }
    }
  });

  // Clean up - remove from DOM
  document.body.removeChild(container);

  return paragraphs;
}

// Enhanced function to convert RGB string to array
function rgbStringToArray(rgbString) {
  if (!rgbString) return [0, 0, 0];

  const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
  }
  return [0, 0, 0];
}

function drawSignatureItem(doc, item, x, y, center = false) {
  const nameWords = item.nameWords || [];
  const labelWords = item.labelWords || [];

  doc.setFont("times", "normal");
  doc.setFontSize(12);

  // Remove pure whitespace tokens for width calculation
  const visibleNameWords = nameWords.filter((w) => !/^\s+$/.test(w.text));

  let nameWidth = 0;
  visibleNameWords.forEach((word) => {
    nameWidth += getWordWidth(doc, word, 12, false, false);
  });

  // Fallback if no name typed
  if (nameWidth === 0) {
    const defaultName = "_________________________";
    nameWidth = doc.getTextWidth(defaultName);

    const textX = center ? x - nameWidth / 2 : x;
    doc.text(defaultName, textX, y);
    doc.line(textX, y + 2, textX + nameWidth, y + 2);
  } else {
    const nameX = center ? x - nameWidth / 2 : x;
    let currentX = nameX;
    let hasUnderline = false;

    nameWords.forEach((word) => {
      // Preserve spaces
      if (/^\s+$/.test(word.text)) {
        const sWidth = doc.getTextWidth(" ");
        currentX += sWidth * word.text.length;
        return;
      }

      let style = "normal";
      if (word.bold && word.italic) style = "bolditalic";
      else if (word.bold) style = "bold";
      else if (word.italic) style = "italic";

      doc.setFont("times", style);
      doc.setFontSize(12);

      doc.text(word.text, currentX, y);

      if (word.underline) hasUnderline = true;

      currentX += doc.getTextWidth(word.text);
    });

    if (hasUnderline) {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(1);
      doc.line(nameX, y + 2, nameX + nameWidth, y + 2);
    }
  }

  // MULTI-LINE LABEL HANDLING
  let currentY = y + 15;
  let lineWords = [];

  labelWords.forEach((word) => {
    if (word.isLineBreak) {
      if (lineWords.length > 0) {
        renderStyledLine(doc, lineWords, x, currentY, center);
        currentY += 12;
        lineWords = [];
      }
    } else {
      lineWords.push(word);
    }
  });

  if (lineWords.length > 0) {
    renderStyledLine(doc, lineWords, x, currentY, center);
    currentY += 12;
  }

  return currentY - y;
}

function renderStyledLine(doc, words, x, y, center) {
  const visibleWords = words.filter((w) => !/^\s+$/.test(w.text));

  if (visibleWords.length === 0) return;

  // Calculate line width
  let lineWidth = 0;
  visibleWords.forEach((w) => {
    lineWidth += getWordWidth(doc, w, 12, false, false);
  });

  // Add spaces between words
  lineWidth += (visibleWords.length - 1) * doc.getTextWidth(" ");

  // Draw line with styling
  const lineX = center ? x - lineWidth / 2 : x;
  let currentX = lineX;

  visibleWords.forEach((word, idx) => {
    let fontStyle = "normal";
    if (word.bold && word.italic) fontStyle = "bolditalic";
    else if (word.bold) fontStyle = "bold";
    else if (word.italic) fontStyle = "italic";

    doc.setFont("times", fontStyle);
    doc.setFontSize(12);

    const wordWidth = doc.getTextWidth(word.text);
    doc.text(word.text, currentX, y);

    if (word.underline) {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(1);
      doc.line(currentX, y + 2, currentX + wordWidth, y + 2);
    }

    currentX += wordWidth;

    // Add space after word (except last word)
    if (idx < visibleWords.length - 1) {
      currentX += doc.getTextWidth(" ");
    }
  });
}

// Enhanced drawJustifiedParagraphs to handle tables with styles
function drawJustifiedParagraphs(
  doc,
  htmlText,
  x,
  y,
  maxWidth,
  lineHeight,
  pageHeight,
  bottomMargin = 40,
  paragraphIndent = 40,
  footerHeight = 0,
  firstParagraphIndent = null,
  listIndent = 40,
  spaceIndentSize = 10,
  tabIndentSize = 40
) {
  const paragraphs = parseHTMLToParagraphs(htmlText);
  const effectiveBottomMargin = bottomMargin + footerHeight;

  paragraphs.forEach((paragraph, paragraphIndex) => {
    // Handle page breaks
    if (paragraph.type === "page-break") {
      doc.addPage();
      y = bottomMargin + 30;
      return;
    }

    // Handle signature rows
    if (paragraph.type === "sig-row") {
      y += 30;

      const row = paragraph.items;

      // Calculate dynamic height based on number of label lines
      let maxLabelLines = 1;
      row.forEach((item) => {
        const labelLines = (item.label || "")
          .split("\n")
          .filter((line) => line.trim() !== "").length;
        maxLabelLines = Math.max(maxLabelLines, labelLines);
      });

      // Base height (name + underline + first line) + extra lines
      const rowHeight = 45 + (maxLabelLines - 1) * 12;
      const blockWidth = maxWidth * 0.8;

      // Page break if needed
      if (y + rowHeight > pageHeight - effectiveBottomMargin) {
        doc.addPage();
        y = bottomMargin + 30;
      }

      // Compute positions
      const startX = x + (maxWidth - blockWidth) / 2;

      if (row.length === 1) {
        // Centered single signature
        const itemX = startX + blockWidth / 2;
        drawSignatureItem(doc, row[0], itemX, y, true);
      } else if (row.length === 2) {
        // Two signatures evenly distributed
        const spacing = blockWidth / 2;
        drawSignatureItem(doc, row[0], startX + spacing * 0.5, y, true);
        drawSignatureItem(doc, row[1], startX + spacing * 1.5, y, true);
      } else if (row.length === 3) {
        // Three signatures evenly distributed
        const spacing = blockWidth / 3;
        drawSignatureItem(doc, row[0], startX + spacing * 0.5, y, true);
        drawSignatureItem(doc, row[1], startX + spacing * 1.5, y, true);
        drawSignatureItem(doc, row[2], startX + spacing * 2.5, y, true);
      } else if (row.length === 4) {
        // Four signatures evenly distributed
        const spacing = blockWidth / 4;
        drawSignatureItem(doc, row[0], startX + spacing * 0.5, y, true);
        drawSignatureItem(doc, row[1], startX + spacing * 1.5, y, true);
        drawSignatureItem(doc, row[2], startX + spacing * 2.5, y, true);
        drawSignatureItem(doc, row[3], startX + spacing * 3.5, y, true);
      }

      y += rowHeight + 40;
      return;
    }

    // Handle tables with enhanced styling
    if (paragraph.type === "table") {
      const estimatedTableHeight = (paragraph.body.length + 2) * 25;
      if (y + estimatedTableHeight > pageHeight - effectiveBottomMargin) {
        doc.addPage();
        y = bottomMargin + 30;
      }

      // Prepare table data
      const headers = paragraph.hasHeader
        ? [paragraph.headers.map((cell) => cell.text)]
        : [];

      const body = paragraph.body.map((row) => row.map((cell) => cell.text));

      // Calculate column widths
      const numColumns = paragraph.hasHeader
        ? paragraph.headers.length
        : paragraph.body[0]?.length || 1;

      const tableWidth = maxWidth;
      const columnWidth = tableWidth / numColumns;

      const columnStyles = {};
      for (let i = 0; i < numColumns; i++) {
        const cellData = paragraph.hasHeader
          ? paragraph.headers[i]
          : paragraph.body[0]?.[i];

        columnStyles[i] = {
          cellWidth: columnWidth,
          halign: cellData?.align || "center",
        };
      }

      // Render table with preserved styles
      doc.autoTable({
        startY: y,
        head: headers,
        body: body,
        styles: {
          fontSize: 10,
          halign: "center",
          cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.5,
          fontStyle: "bold",
        },
        theme: "grid",
        margin: { left: x, right: x },
        columnStyles: columnStyles,
        tableLineWidth: 0.5,
        tableLineColor: [0, 0, 0],
        didParseCell: function (data) {
          const rowIndex = data.row.index;
          const colIndex = data.column.index;

          // Apply styling from parsed data
          if (data.section === "body" && paragraph.body[rowIndex]) {
            const cell = paragraph.body[rowIndex][colIndex];
            if (cell) {
              if (cell.isBold) {
                data.cell.styles.fontStyle = "bold";
              }
              if (cell.backgroundColor) {
                const bgColor = rgbStringToArray(cell.backgroundColor);
                data.cell.styles.fillColor = bgColor;
              }
              if (cell.textColor) {
                const textColor = rgbStringToArray(cell.textColor);
                data.cell.styles.textColor = textColor;
              }
              if (cell.align) {
                data.cell.styles.halign = cell.align;
              }
            }
          }

          // Apply header styles
          if (data.section === "head" && paragraph.hasHeader) {
            const cell = paragraph.headers[colIndex];
            if (cell) {
              if (cell.backgroundColor) {
                const bgColor = rgbStringToArray(cell.backgroundColor);
                data.cell.styles.fillColor = bgColor;
              }
              if (cell.textColor) {
                const textColor = rgbStringToArray(cell.textColor);
                data.cell.styles.textColor = textColor;
              }
            }
          }
        },
      });

      y = doc.lastAutoTable.finalY + 20;
      return;
    }

    // Rest of the paragraph rendering logic (unchanged)
    let lineWords = [];
    let lineWidth = 0;
    let isFirstLineOfParagraph = true;

    const whitespaceIndent = (paragraph.totalSpaces || 0) * spaceIndentSize;

    let currentParagraphIndent;
    let bulletOffset = 0;
    let bulletText = "";
    let listLeftMargin = 0;

    let fontSize = 12;
    let isBold = false;

    if (paragraph.type === "header") {
      const headerSizes = {
        1: 24,
        2: 20,
        3: 16,
        4: 14,
        5: 12,
        6: 10,
      };
      fontSize = headerSizes[paragraph.level] || 12;
      isBold = true;

      if (whitespaceIndent > 0) {
        currentParagraphIndent = whitespaceIndent;
      } else {
        currentParagraphIndent =
          paragraphIndex === 0 && firstParagraphIndent !== null
            ? firstParagraphIndent
            : 0;
      }
    } else if (paragraph.type === "blockquote") {
      // Blockquotes get indented from both sides
      currentParagraphIndent = 0;
    } else if (paragraph.type === "ordered-list") {
      bulletText = `${paragraph.number}. `;
      bulletOffset = 25;
      currentParagraphIndent = 0;
      listLeftMargin = whitespaceIndent > 0 ? whitespaceIndent : listIndent;
    } else if (paragraph.type === "unordered-list") {
      bulletText = "• ";
      bulletOffset = 20;
      currentParagraphIndent = 0;
      listLeftMargin = whitespaceIndent > 0 ? whitespaceIndent : listIndent;
    } else {
      if (whitespaceIndent > 0) {
        currentParagraphIndent = whitespaceIndent;
      } else {
        currentParagraphIndent =
          paragraphIndex === 0 && firstParagraphIndent !== null
            ? firstParagraphIndent
            : paragraphIndent;
      }
    }

    const flushLine = (lineWords, isLastLine = false, isFirstLine = false) => {
      if (lineWords.length === 0) return y;

      const visibleWords = lineWords.filter(
        (w) => !/^\s+$/.test(w.text) && !w.isLineBreak
      );

      if (visibleWords.length === 0) {
        y += lineHeight;
        return y;
      }

      let startX = x;
      let effectiveMaxWidth = maxWidth;

      const alignment = paragraph.alignment || "left";

      // Handle blockquote FIRST - applies to ALL lines
      if (paragraph.type === "blockquote") {
        const blockquoteIndent = 40;
        startX = x + blockquoteIndent;
        effectiveMaxWidth = maxWidth - blockquoteIndent * 2;
      } else if (
        isFirstLine &&
        (paragraph.type === "ordered-list" ||
          paragraph.type === "unordered-list")
      ) {
        doc.setFont("times", "normal");
        doc.setFontSize(fontSize);
        startX = x + listLeftMargin;
        doc.text(bulletText, startX, y);
        startX = x + listLeftMargin + bulletOffset;
        effectiveMaxWidth = maxWidth - (startX - x);
      } else if (
        isFirstLine &&
        (paragraph.type === "paragraph" || paragraph.type === "header")
      ) {
        startX = x + currentParagraphIndent;
        effectiveMaxWidth = maxWidth - (startX - x);
      } else if (
        paragraph.type === "ordered-list" ||
        paragraph.type === "unordered-list"
      ) {
        startX = x + listLeftMargin + bulletOffset;
        effectiveMaxWidth = maxWidth - (startX - x);
      } else {
        startX = x;
        effectiveMaxWidth = maxWidth;
      }

      const totalWordsWidth = visibleWords.reduce(
        (acc, w) =>
          acc + getWordWidth(doc, w, fontSize, isBold || w.bold, w.italic),
        0
      );

      const numGaps = visibleWords.length - 1;
      let extraSpace = 0;
      let alignmentOffset = 0;

      if (alignment === "center") {
        alignmentOffset = (effectiveMaxWidth - totalWordsWidth) / 2;
      } else if (alignment === "right") {
        alignmentOffset = effectiveMaxWidth - totalWordsWidth;
      } else if (alignment === "justify" || alignment === "left") {
        extraSpace =
          !isLastLine && numGaps > 0 && alignment === "justify"
            ? (effectiveMaxWidth - totalWordsWidth) / numGaps
            : 0;
      }

      let currentX = startX + alignmentOffset;
      let visibleWordIndex = 0;

      for (let i = 0; i < lineWords.length; i++) {
        const word = lineWords[i];
        const isSpace = /^\s+$/.test(word.text);
        const isLineBreak = word.isLineBreak;

        if (isLineBreak) {
          break;
        } else if (!isSpace) {
          let fontStyle = "normal";
          const wordBold = isBold || word.bold;
          const wordItalic =
            paragraph.type === "blockquote" ? true : word.italic;
          if (wordBold && wordItalic) fontStyle = "bolditalic";
          else if (wordBold) fontStyle = "bold";
          else if (wordItalic) fontStyle = "italic";

          doc.setFont("times", fontStyle);
          doc.setFontSize(fontSize);

          const wordWidth = doc.getTextWidth(word.text);

          if (word.underline) {
            doc.line(currentX, y + 1, currentX + wordWidth, y + 1);
          }

          doc.text(word.text, currentX, y);
          currentX += wordWidth;

          if (
            alignment === "justify" &&
            !isLastLine &&
            visibleWordIndex < numGaps
          ) {
            currentX += extraSpace;
          } else if (
            alignment !== "justify" &&
            visibleWordIndex < visibleWords.length - 1
          ) {
            doc.setFont("times", "normal");
            doc.setFontSize(fontSize);
            currentX += doc.getTextWidth(" ");
          }

          visibleWordIndex++;
        } else {
          if (isLastLine || alignment !== "justify") {
            doc.setFont("times", "normal");
            doc.setFontSize(fontSize);
            currentX += doc.getTextWidth(" ");
          }
        }
      }

      y += lineHeight * (fontSize / 12);

      if (y + lineHeight > pageHeight - effectiveBottomMargin) {
        doc.addPage();
        y = bottomMargin + 30;
      }

      return y;
    };

    const paragraphWords = paragraph.words;

    for (let i = 0; i < paragraphWords.length; i++) {
      const word = paragraphWords[i];

      if (word.isLineBreak) {
        if (lineWords.length > 0) {
          y = flushLine(lineWords, true, isFirstLineOfParagraph);
        } else {
          y += lineHeight;
        }
        lineWords = [];
        lineWidth = 0;
        isFirstLineOfParagraph = false;
        continue;
      }

      const wordWidth = getWordWidth(doc, word, fontSize, isBold, false);

      let availableWidth;
      if (isFirstLineOfParagraph) {
        if (paragraph.type === "paragraph" || paragraph.type === "header") {
          availableWidth = maxWidth - currentParagraphIndent;
        } else {
          availableWidth = maxWidth - listLeftMargin - bulletOffset;
        }
      } else {
        if (
          paragraph.type === "ordered-list" ||
          paragraph.type === "unordered-list"
        ) {
          availableWidth = maxWidth - listLeftMargin - bulletOffset;
        } else {
          availableWidth = maxWidth;
        }
      }

      const spaceWidth = lineWords.length > 0 ? doc.getTextWidth(" ") : 0;

      if (
        lineWidth + spaceWidth + wordWidth > availableWidth &&
        lineWords.length > 0
      ) {
        y = flushLine(lineWords, false, isFirstLineOfParagraph);
        lineWords = [];
        lineWidth = 0;
        isFirstLineOfParagraph = false;
      }

      lineWords.push(word);
      lineWidth += wordWidth + spaceWidth;
    }

    if (lineWords.length > 0) {
      y = flushLine(lineWords, true, isFirstLineOfParagraph);
    }

    if (paragraphIndex < paragraphs.length - 1) {
      const nextParagraph = paragraphs[paragraphIndex + 1];
      const isCurrentList =
        paragraph.type === "ordered-list" ||
        paragraph.type === "unordered-list";
      const isNextList =
        nextParagraph.type === "ordered-list" ||
        nextParagraph.type === "unordered-list";
      const isCurrentHeader = paragraph.type === "header";
      const isNextHeader = nextParagraph.type === "header";
      const isCurrentBlockquote = paragraph.type === "blockquote";
      const isNextBlockquote = nextParagraph.type === "blockquote";

      // Add extra spacing after blockquotes
      if (isCurrentBlockquote) {
        y += lineHeight * 1.5;

        // Add extra spacing before blockquotes
      } else if (isNextBlockquote) {
        y += lineHeight * 0.5;

        // List → list (slightly tighter)
      } else if (isCurrentList && isNextList) {
        y += lineHeight * 0.3;

        // Headers
      } else if (isCurrentHeader || isNextHeader) {
        y += lineHeight * 1.0;

        // List → non-list (extra space after list)
      } else if (isCurrentList && !isNextList) {
        y += lineHeight * 1.0;

        // Non-list → list (give breathing room before a list)
      } else if (!isCurrentList && isNextList) {
        y += lineHeight * 1.0;

        // Default spacing
      } else {
        y += lineHeight * 0.5;
      }
    }
  });

  return y;
}

function parseNodeToSegments(node, style = {}, stripLeading = false) {
  const words = [];
  let isFirstTextNode = true;

  function processNode(node, style = {}) {
    if (node.nodeType === Node.TEXT_NODE) {
      let textContent = node.textContent;

      if (stripLeading && isFirstTextNode) {
        textContent = textContent.replace(/^[\s\t]+/, "");
        isFirstTextNode = false;
      }

      const rawWords = textContent.split(/(\s+)/);
      rawWords.forEach((word) => {
        if (word !== "") {
          words.push({
            text: word,
            bold: style.bold || false,
            italic: style.italic || false,
            underline: style.underline || false,
          });
        }
      });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      const classList = node.className || "";

      if (classList.includes("ql-ui")) {
        return;
      }

      const newStyle = { ...style };

      if (tag === "b" || tag === "strong") newStyle.bold = true;
      if (tag === "i" || tag === "em") newStyle.italic = true;
      if (tag === "u") newStyle.underline = true;

      if (tag === "span" || tag === "a" || tag === "code") {
        const inlineStyle = node.style;
        if (inlineStyle) {
          if (
            inlineStyle.fontWeight === "bold" ||
            parseInt(inlineStyle.fontWeight) >= 700
          ) {
            newStyle.bold = true;
          }
          if (inlineStyle.fontStyle === "italic") {
            newStyle.italic = true;
          }
          if (
            inlineStyle.textDecoration &&
            inlineStyle.textDecoration.includes("underline")
          ) {
            newStyle.underline = true;
          }
        }
      }

      if (tag === "br") {
        words.push({
          text: "\n",
          bold: false,
          italic: false,
          underline: false,
          isLineBreak: true,
        });
        return;
      }

      node.childNodes.forEach((child) => processNode(child, newStyle));
    }
  }

  processNode(node, style);
  return words;
}

function getWordWidth(
  doc,
  word,
  fontSize = 12,
  forceBold = false,
  forceItalic = false
) {
  let fontStyle = "normal";
  const isBold = forceBold || word.bold;
  const isItalic = forceItalic || word.italic;

  if (isBold && isItalic) fontStyle = "bolditalic";
  else if (isBold) fontStyle = "bold";
  else if (isItalic) fontStyle = "italic";

  doc.setFont("times", fontStyle);
  doc.setFontSize(fontSize);

  return doc.getTextWidth(word.text);
}

// Helper function to split HTML by page breaks (<hr> tags)
function splitHTMLByPageBreaks(htmlString) {
  if (!htmlString) return [];

  // Split by <hr> tags which represent page breaks
  const sections = htmlString.split(/<hr\s*\/?>/gi);
  return sections.filter((section) => section.trim() !== "");
}

// Simplified function to render notarization content
async function renderNotarizationContent(
  doc,
  notarizationContent,
  contentMargin,
  pageWidth,
  pageHeight,
  footerHeight = 0
) {
  if (!notarizationContent || notarizationContent.trim() === "") {
    return;
  }

  // Add a new page for notarization
  doc.addPage();
  let currentY = 80;

  const textWidth = pageWidth - 2 * contentMargin;
  const lineHeight = 14;

  // Split content by page breaks
  const sections = splitHTMLByPageBreaks(notarizationContent);

  for (let i = 0; i < sections.length; i++) {
    if (i > 0) {
      doc.addPage();
      currentY = 80;
    }

    // Render this section using drawJustifiedParagraphs
    currentY = drawJustifiedParagraphs(
      doc,
      sections[i],
      contentMargin,
      currentY,
      textWidth,
      lineHeight,
      pageHeight,
      40,
      0,
      footerHeight,
      0,
      40,
      5,
      40
    );
  }
}

export {
  calculateDynamicColumnWidths,
  processDynamicTableData,
  styleDynamicTableCell,
  generateDynamicTable,
  parseHTMLToParagraphs,
  drawJustifiedParagraphs,
  parseNodeToSegments,
  getWordWidth,
  rgbStringToArray,
  splitHTMLByPageBreaks,
  renderNotarizationContent,
};

// // Get the actual current page number from the document
//       const currentPageNumber = doc.internal.getNumberOfPages();
//       let ackTexts = formData.notarization.notaryText.nt3 || "";

//       // Replace @page with the current page number
//       ackTexts = ackTexts.replace(/@page/g, currentPageNumber);
